import React from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { Check, User, Camera, ArrowRight } from "lucide-react";

const HERO_BRIDES = "https://customer-assets.emergentagent.com/job_lovers-collective/artifacts/z1wc8rrt_Arlia%20and%20Tara%20%282%29.png";
const INTIMATE = "https://customer-assets.emergentagent.com/job_lovers-collective/artifacts/1prnc1o4_nicci%20k%20photography-114.jpg";

const LOVER_BULLETS = [
  { strong: "Make a quick profile.", rest: "Drop in up to 8 photos so vendors can see your vibe." },
  { strong: "Share what you want.", rest: "It's entirely up to you if you want to share your pronouns, identities, location, or what kinds of projects you're open to." },
  { strong: "Get found.", rest: "Wedding pros will reach out when they're planning styled shoots or content days." },
  { strong: "You hold the keys.", rest: "Say yes to what looks fun, ignore what doesn't, and change or hide your profile whenever you feel like it." },
];

const VENDOR_BULLETS = [
  { strong: "Join the community.", rest: "Fill out a quick application - we vet everyone and usually approve you within 24 hours." },
  { strong: "Find the right fit.", rest: "Search for real lovers by location, identity, language, and more." },
  { strong: "Put out a call.", rest: "Post your upcoming styled shoots, content days, or editorial concepts." },
  { strong: "Chat directly.", rest: "Connect straight with the lovers you want to work with - no gatekeepers or middle layers." },
];

// Chips alternate through the locked palette for color variation
const REP_AREAS = [
  { label: "LGBTQIA+",                         tone: "lilac" },
  { label: "Body Diverse",                     tone: "mustard" },
  { label: "Gender Expansive",                 tone: "powder" },
  { label: "BIPOC",                            tone: "wine" },
  { label: "Expansive Relationship Dynamics",  tone: "mustard" },
  { label: "Visible & Invisible Disabilities", tone: "lilac" },
  { label: "Intercultural Lovers",             tone: "powder" },
];

const chipClass = (tone) => {
  switch (tone) {
    case "wine":    return "lif-chip bg-wine text-white border-black";
    case "mustard": return "lif-chip-mustard";
    case "powder":  return "lif-chip-powder";
    case "lilac":   return "lif-chip-lilac";
    default:        return "lif-chip-cream";
  }
};

export default function Landing() {
  return (
    <Layout>
      {/* ===== HERO ===== */}
      <section>
        <div className="max-w-[1280px] mx-auto px-6 md:px-10 pt-10 md:pt-14 pb-20 md:pb-24">
          <div className="grid grid-cols-12 gap-6 md:gap-10 items-stretch">
            <div className="col-span-12 md:col-span-6 animate-fade-up flex flex-col pt-2 md:pt-4">
              <p className="lif-overline mb-6">A Global Database</p>
              <h1
                className="text-[3.2rem] sm:text-[4rem] lg:text-[5.4rem] leading-[0.95] text-black tracking-tight"
                style={{ fontFamily: '"Bringbold", Georgia, serif' }}
              >
                <span className="block">Real love,</span>
                <span className="block">Fully Represented.</span>
              </h1>
              <p className="mt-7 text-base md:text-lg text-black/85 max-w-xl leading-relaxed">
                A directory connecting real, diverse lovers with wedding professionals who want their styled
                shoots, editorials and content days to reflect the world as it actually is.
              </p>

              <div className="mt-8 flex flex-wrap items-center gap-3">
                <Link to="/join?role=couple" data-testid="hero-cta-lover" className="lif-btn-lilac">
                  <User className="w-4 h-4" strokeWidth={2}/>
                  I'm a lover - add me
                </Link>
                <Link to="/join?role=vendor" data-testid="hero-cta-vendor" className="lif-btn-powder">
                  <Camera className="w-4 h-4" strokeWidth={2}/>
                  I'm a wedding pro
                </Link>
              </div>

              <div className="mt-auto pt-10 flex items-center gap-4 text-sm text-black/75">
                <div className="flex -space-x-2">
                  <span className="w-9 h-9 rounded-full bg-lilac border-2 border-black"/>
                  <span className="w-9 h-9 rounded-full bg-mustard border-2 border-black"/>
                  <span className="w-9 h-9 rounded-full bg-powder border-2 border-black"/>
                </div>
                500+ diverse lovers already on the platform - and growing.
              </div>
            </div>

            {/* Hero image - sized to match content column height, centered on the touching hands */}
            <div className="col-span-12 md:col-span-6">
              <div className="relative rounded-[1.5rem] overflow-hidden border-2 border-black h-full min-h-[480px] md:min-h-[560px]">
                <img
                  src={HERO_BRIDES}
                  alt="Two brides celebrating on a coastal cliff, hands raised together"
                  className="absolute inset-0 w-full h-full object-cover"
                  style={{ objectPosition: "55% 32%" }}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== HOW IT WORKS ===== */}
      <section className="bg-powder border-y-2 border-black py-20 md:py-24">
        <div className="max-w-[1280px] mx-auto px-6 md:px-10">
          <p className="lif-overline mb-5">How It Works</p>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-black leading-[1.05] max-w-3xl tracking-tight">
            Two sides of the story.<br/>One beautifully diverse wedding industry.
          </h2>

          <div className="mt-12 grid md:grid-cols-2 gap-7">
            <div className="bg-lilac border-2 border-black rounded-[1.5rem] p-9 md:p-10 flex flex-col">
              <p className="lif-overline mb-5">For Lovers</p>
              <h3 className="font-serif text-2xl md:text-[1.85rem] text-black leading-snug tracking-tight">
                Put yourself out there for shoots and editorials - totally on your own terms.
              </h3>
              <ul className="mt-7 space-y-4 flex-1">
                {LOVER_BULLETS.map((b, i) => (
                  <li key={i} className="flex items-start gap-3 text-black">
                    <Check className="w-5 h-5 mt-0.5 shrink-0" strokeWidth={2.5}/>
                    <p className="leading-relaxed">
                      <strong className="font-semibold">{b.strong}</strong>{" "}
                      <span className="text-black/85">{b.rest}</span>
                    </p>
                  </li>
                ))}
              </ul>
              <Link to="/join?role=couple" className="lif-btn-cream mt-9 self-start" data-testid="how-cta-lover">
                Add my listing <ArrowRight className="w-4 h-4" strokeWidth={2}/>
              </Link>
            </div>

            <div className="bg-mustard border-2 border-black rounded-[1.5rem] p-9 md:p-10 flex flex-col">
              <p className="lif-overline mb-5">For Vendors</p>
              <h3 className="font-serif text-2xl md:text-[1.85rem] text-black leading-snug tracking-tight">
                Stop using models and stand-ins. Start featuring real, wildly in love, lovers.
              </h3>
              <ul className="mt-7 space-y-4 flex-1">
                {VENDOR_BULLETS.map((b, i) => (
                  <li key={i} className="flex items-start gap-3 text-black">
                    <Check className="w-5 h-5 mt-0.5 shrink-0" strokeWidth={2.5}/>
                    <p className="leading-relaxed">
                      <strong className="font-semibold">{b.strong}</strong>{" "}
                      <span className="text-black/85">{b.rest}</span>
                    </p>
                  </li>
                ))}
              </ul>
              <Link to="/join?role=vendor" className="lif-btn-cream mt-9 self-start" data-testid="how-cta-vendor">
                Apply as a vendor <ArrowRight className="w-4 h-4" strokeWidth={2}/>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ===== WHY WE EXIST ===== */}
      <section className="py-20 md:py-24">
        <div className="max-w-[1280px] mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-10 items-stretch">
          <div className="md:col-span-5 flex">
            <div className="relative rounded-[1.5rem] overflow-hidden border-2 border-black w-full min-h-[440px]">
              <img src={INTIMATE} alt="Intimate close-up of a couple" className="absolute inset-0 w-full h-full object-cover"/>
            </div>
          </div>
          <div className="md:col-span-7 flex flex-col justify-center">
            <p className="lif-overline mb-5">Why We Exist</p>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-[3.7rem] text-black leading-[1.02] tracking-tight">
              The wedding industry has a representation problem. We're here to change that.
            </h2>
            <p className="mt-6 text-black/85 leading-relaxed">
              Styled shoots are what fill our magazines, Pinterest boards, and portfolios - but if we're being
              honest, they're still showing the exact same narrow version of love over and over again.
            </p>
            <p className="mt-4 text-black/85 leading-relaxed">
              That isn't what the wedding community actually looks like. That's just what's been given the
              spotlight. Love, in Full is here to shift that focus by creating a space where wedding pros can
              easily find and collaborate with real, diverse lovers. No models, no stand-ins - just real people
              whose love stories deserve to be seen.
            </p>

            <div className="mt-8 flex flex-wrap gap-2.5">
              {REP_AREAS.map((c) => (
                <span key={c.label} className={chipClass(c.tone)}>{c.label}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ===== FINAL CTA ===== */}
      <section className="bg-lilac border-y-2 border-black">
        <div className="max-w-[1280px] mx-auto px-6 md:px-10 py-20 md:py-24 text-center">
          <p className="lif-overline mb-5">Be On The Platform</p>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-[3.6rem] text-black leading-tight tracking-tight">
            or build from it.
          </h2>
          <p className="mt-5 text-black/90 max-w-xl mx-auto text-lg">
            Either way, you're helping reshape what wedding looks like.
          </p>
          <div className="mt-9 flex flex-wrap items-center justify-center gap-3">
            <Link to="/join?role=couple" data-testid="final-cta-lover" className="lif-btn-cream">
              Add my listing <ArrowRight className="w-4 h-4" strokeWidth={2}/>
            </Link>
            <Link to="/join?role=vendor" data-testid="final-cta-vendor" className="lif-btn-powder">
              I'm a wedding vendor <ArrowRight className="w-4 h-4" strokeWidth={2}/>
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
}
